import React, { useState, useRef, useEffect } from "react";

// Custom scrollbar styles
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #ccc;
  }
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #ddd #f1f1f1;
  }
`;

const TableTop = ({ 
  searchTerm, 
  setSearchTerm, 
  sortField, 
  setSortField, 
  selectedAdvisor, 
  setSelectedAdvisor, 
  dateFilter, 
  handleDateFilterChange, 
  sortOrder, 
  toggleSortOrder, 
  legalAdvisors, 
  handleSearch,
  clearSearch,
  userRole,
  isLoadingAdvisors,
  setShowModal
}) => {
  // State for custom advisors dropdown
  const [isAdvisorDropdownOpen, setIsAdvisorDropdownOpen] = useState(false);
  const advisorDropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (advisorDropdownRef.current && !advisorDropdownRef.current.contains(event.target)) {
        setIsAdvisorDropdownOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  
  // Get selected advisor name for display
  const getSelectedAdvisorName = () => {
    if (!selectedAdvisor) return isLoadingAdvisors ? "Loading all advisors..." : "All Advisors";
    
    const advisor = legalAdvisors.find(a => a.id.toString() === selectedAdvisor.toString());
    return advisor ? advisor.name : "Selected Advisor";
  };
  
  return (
    <div className='flex flex-col space-y-4 pt-[8px] pb-[18px] border-b border-gray-200  w-full'>
      {/* Inject scrollbar styles */}
      <style>{scrollbarStyles}</style>
      
      {/* First Row of Filters */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 w-full'>
        {/* Search input - Updated with form and clear button */}
        <div className='w-full'>
          <form onSubmit={handleSearch}>
            <div className='relative'>
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type='text'
                placeholder='Search by name or email...'
                value={searchTerm}
                style={{ boxShadow: 'none' }}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='h-12 w-full pl-10 pr-4 rounded-md border border-gray-300 bg-white text-gray-700 placeholder:text-gray-400 text-sm hover:border-gray-400 focus:border-[#F16745] focus:outline-none focus:ring-0 transition-colors'
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-[46px] top-0 h-full px-2 flex items-center justify-center text-gray-400 hover:text-gray-600"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              <button
                type="submit"
                className='absolute top-0 right-0 flex h-full w-[46px] items-center justify-center bg-[#F16745] text-white rounded-r-md'>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.2687 15.6657L12.6281 11.897C14.5406 9.28135 14.3437 5.54072 11.9531 3.17822C10.6875 1.9126 8.99995 1.20947 7.19995 1.20947C5.39995 1.20947 3.71245 1.9126 2.44683 3.17822C-0.168799 5.79385 -0.168799 10.0688 2.44683 12.6845C3.71245 13.9501 5.39995 14.6532 7.19995 14.6532C8.91558 14.6532 10.5187 14.0063 11.7843 12.8532L16.4812 16.6501C16.5937 16.7345 16.7343 16.7907 16.875 16.7907C17.0718 16.7907 17.2406 16.7063 17.3531 16.5657C17.5781 16.2845 17.55 15.8907 17.2687 15.6657ZM7.19995 13.3876C5.73745 13.3876 4.38745 12.8251 3.34683 11.7845C1.20933 9.64697 1.20933 6.1876 3.34683 4.07822C4.38745 3.0376 5.73745 2.4751 7.19995 2.4751C8.66245 2.4751 10.0125 3.0376 11.0531 4.07822C13.1906 6.21572 13.1906 9.6751 11.0531 11.7845C10.0406 12.8251 8.66245 13.3876 7.19995 13.3876Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          </form>
        </div>

        {/* Sort by dropdown */}
        <div className='w-full'>
          <div className="relative">
            <select
              value={sortField}
              onChange={(e) => setSortField(e.target.value)}
              className='h-12 w-full px-4 pl-4 pr-10 rounded-md border border-gray-300 bg-white text-gray-700 text-sm hover:border-gray-400 focus:outline-none focus:ring-0 focus:border-[#F16745] transition-colors'
              style={{ 
                backgroundImage: 'none', 
                WebkitAppearance: 'none', 
                MozAppearance: 'none',
                appearance: 'none'
              }}
            >
              <option value="">Sort by</option>
              <option value="name">Name</option>
              <option value="email">Email</option>
              <option value="progress">Progress</option>
            </select>
            <div className="flex items-center pointer-events-none absolute inset-y-0 right-0 px-3 space-x-1">
              <svg className="h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <svg className="w-5 h-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>

        {/* Custom Advisors dropdown with scrollable list */}
        <div className='w-full'>
          <div ref={advisorDropdownRef} className="relative">
            <button
              type="button"
              disabled={isLoadingAdvisors}
              onClick={() => setIsAdvisorDropdownOpen(!isAdvisorDropdownOpen)}
              className="flex justify-between items-center h-12 w-full px-4 rounded-md border border-gray-300 bg-white text-gray-700 text-sm hover:border-gray-400 focus:border-[#F16745] focus:outline-none focus:ring-0 transition-colors"
            >
              <span className="truncate">
                {getSelectedAdvisorName()}
              </span>
              <div className="flex items-center">
                {isLoadingAdvisors ? (
                  <div className="animate-spin h-4 w-4 border-2 border-[#F16745] border-t-transparent rounded-full mr-1" />
                ) : null}
                <svg className="w-5 h-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </button>
            
            {/* Dropdown menu with scrollable container */}
            {isAdvisorDropdownOpen && (
              <div className="absolute  mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden">
               
                <div className="max-h-60 overflow-y-auto py-1 custom-scrollbar">
                  
                  {legalAdvisors.length === 0 ? "No advisors available" : legalAdvisors.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedAdvisor("");
                        setIsAdvisorDropdownOpen(false);
                      }}
                      className={`block w-full text-left px-4 py-2 text-sm ${!selectedAdvisor ? 'bg-gray-100 text-[#F16745] ' : 'text-gray-900 hover:bg-gray-50'}`}
                    > 
                      {isLoadingAdvisors ? "Loading all advisors..." : "All Advisors"}
                    </button>
                  )}
                  {/* <button
                    type="button"
                    onClick={() => {
                      setSelectedAdvisor("");
                      setIsAdvisorDropdownOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-sm ${!selectedAdvisor ? 'bg-gray-100 text-[#F16745] ' : 'text-gray-900 hover:bg-gray-50'}`}
                  >
                    {isLoadingAdvisors ? "Loading all advisors..." : 
                     legalAdvisors.length === 0 ? "No advisors available" : 
                     legalAdvisors.length === 1 ? "Filter by advisor" : "All Advisors"}
                  </button> */}
                  
                  {!isLoadingAdvisors && legalAdvisors.map((advisor) => (
                    <button
                      key={advisor.id}
                      type="button"
                      onClick={() => {
                        setSelectedAdvisor(advisor.id.toString());
                        setIsAdvisorDropdownOpen(false);
                      }}
                      className={`block w-full text-left px-4 py-2 text-sm ${selectedAdvisor === advisor.id.toString() ? 'bg-gray-100 text-[#F16745]' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      {advisor.name}
                    </button>
                  ))}
                </div>
                
                {/* Show count at the bottom if admin and has advisors */}
                {userRole?.toLowerCase() === "admin" && legalAdvisors.length > 0 && !isLoadingAdvisors && (
                  <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-700">
                    {legalAdvisors.length} advisors available
                  </div>
                )}
              </div>
            )}
            
            {/* Loading tooltip for admin role */}
            {isLoadingAdvisors && userRole?.toLowerCase() === "admin" && (
              <div className="absolute top-14 right-0 z-10 bg-gray-800 text-white text-xs p-2 rounded shadow-lg">
                Loading all advisors for better filtering
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Second Row of Filters */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 w-full'>
        {/* Date filter dropdown */}
        <div className='w-full'>
          <div className="relative">
            <select
              value={dateFilter}
              onChange={(e) => handleDateFilterChange(e.target.value)}
              className='h-12 w-full px-4 pl-4 pr-10 rounded-md border border-gray-300 bg-white text-gray-700 text-sm hover:border-gray-400 focus:outline-none focus:ring-0 focus:border-[#F16745] transition-colors'
              style={{ 
                backgroundImage: 'none', 
                WebkitAppearance: 'none', 
                MozAppearance: 'none',
                appearance: 'none'
              }}
              key={`date-filter-${dateFilter}`}
              onClick={(e) => {
                if (e.target.tagName === "OPTION" && e.target.value === "custom") {
                  setShowModal(true);
                }
              }}
            >
              <option value="">All Dates</option>
              <option value="today">Today</option>
              <option value="this_week">This Week</option>
              <option value="this_month">This Month</option>
              <option value="this_year">This Year</option>
              <option value="custom">Custom Date</option>
            </select>
            <div className="flex items-center pointer-events-none absolute inset-y-0 right-0 px-3 space-x-1">
              <svg className="h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <svg className="w-5 h-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>

        {/* Sort direction button - now shown whenever sortField has a value */}
        {sortField && (
          <div className='w-full'>
            <button
              onClick={toggleSortOrder}
              className='h-12 w-full flex items-center justify-center space-x-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm hover:bg-gray-50 transition-colors'
            >
              <span>{sortOrder === "asc" ? "Descending" : "Ascending"}</span>
              <svg className={`w-4 h-4 text-gray-500 ${sortOrder === "desc" ? "transform rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TableTop; 